import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Settings, Plus, Trash2 } from "lucide-react";

export default function CloseReasonsSettingsDialog({ open, onOpenChange, onSaved }) {
  const [items, setItems] = useState([]);
  const [newReason, setNewReason] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setNewReason("");
    (async () => {
      try {
        const { data } = await api.get("/settings");
        setItems(data.close_reasons || []);
      } catch (e) { toast.error(formatApiError(e)); }
    })();
  }, [open]);

  const addOne = () => {
    const v = newReason.trim();
    if (!v) return;
    if (items.includes(v)) { toast.error("Already in list"); return; }
    setItems([...items, v]);
    setNewReason("");
  };
  const removeAt = (i) => setItems(items.filter((_, idx) => idx !== i));
  const editAt = (i, v) => setItems(items.map((x, idx) => (idx === i ? v : x)));

  const save = async () => {
    const cleaned = items.map((x) => x.trim()).filter(Boolean);
    if (!cleaned.length) { toast.error("Add at least one reason"); return; }
    setSaving(true);
    try {
      await api.put("/settings", { close_reasons: cleaned });
      toast.success("Close reasons saved");
      onSaved?.();
      onOpenChange(false);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto" data-testid="close-reasons-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <Settings size={18} className="text-slate-700" /> Follow-up close reasons
          </DialogTitle>
          <DialogDescription>Add, edit or remove the reasons shown in the Close Follow-up dropdown.</DialogDescription>
        </DialogHeader>

        <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
          {items.map((r, i) => (
            <div key={i} className="flex items-center gap-2" data-testid={`close-reason-row-${i}`}>
              <Input value={r} onChange={(e) => editAt(i, e.target.value)}
                     data-testid={`close-reason-input-${i}`} />
              <Button type="button" size="icon" variant="ghost"
                      className="text-slate-400 hover:text-red-600"
                      onClick={() => removeAt(i)} data-testid={`close-reason-remove-${i}`}>
                <Trash2 size={16} />
              </Button>
            </div>
          ))}
        </div>

        <div className="flex items-center gap-2 pt-2 border-t border-slate-100">
          <Input value={newReason} placeholder="Add a new reason..."
                 onChange={(e) => setNewReason(e.target.value)}
                 onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addOne())}
                 data-testid="close-reason-new-input" />
          <Button type="button" onClick={addOne} data-testid="close-reason-add-button"
                  className="bg-sky-500 hover:bg-sky-600 text-white">
            <Plus size={16} className="mr-1" /> Add
          </Button>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}
                  data-testid="close-reasons-cancel">Cancel</Button>
          <Button type="button" disabled={saving} onClick={save} data-testid="close-reasons-save"
                  className="bg-slate-900 hover:bg-slate-800 text-white">
            {saving ? "Saving..." : "Save reasons"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
